import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  copyStorePaymentOptions,
  getStorePaymentOptions,
  saveStorePaymentOptions,
  getCountryPaymentOptions,
} from '../../../../utils/service-calls/payment-setup';
import { ButtonBlack, StoreSelect } from '../../../reusable';

const MODES = { CONFIGURE: 'CONFIGURE', COPY: 'COPY' };
const MODE_LABELS = { [MODES.CONFIGURE]: 'Configure Store', [MODES.COPY]: 'Copy Store' };

const emptyRow = () => ({
  mediaType: '',
  paymentOptionType: '',
  paymentType: '',
  rowId: uuidv4(),
});

const isDuplicateRow = (rows, candidate) => rows.some(
  (r) => r.mediaType === candidate.mediaType
         && r.paymentOptionType === candidate.paymentOptionType
         && r.paymentType === candidate.paymentType,
);

const makeOptionKey = (o) => `${o.mediaType}|${o.paymentOptionType}|${o.paymentType}`;

const PaymentConfigTab = ({
  selectedCountry, hasNextTab, onSaveAndNext, onSaveStatus,
}) => {
  const userToken = useSelector((state) => state.authorizationReducer.auth.accessToken);

  const [mode, setMode] = useState(MODES.COPY);
  const [, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Country reference options (shared) ---
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOptionsLoaded, setCountryOptionsLoaded] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);

  // --- Copy Store state ---
  const [selectedSourceStore, setSelectedSourceStore] = useState(null);
  const [selectedDestStore, setSelectedDestStore] = useState(null);
  const [override, setOverride] = useState(false);
  const [excludeTypes, setExcludeTypes] = useState([]);
  const [sourceOptions, setSourceOptions] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [destOptions, setDestOptions] = useState(null);
  const [destLoading, setDestLoading] = useState(false);

  // --- Configure Store state ---
  const [selectedConfigStore, setSelectedConfigStore] = useState(null);
  const [configFetched, setConfigFetched] = useState(false);
  const [configRows, setConfigRows] = useState(() => [emptyRow()]);
  const [configLoading, setConfigLoading] = useState(false);
  const [emptyStoreMessage, setEmptyStoreMessage] = useState('');

  // --- Collapsible sections ---
  const [countryTableExpanded, setCountryTableExpanded] = useState(true);
  const [configTableExpanded, setConfigTableExpanded] = useState(true);

  // Fetch country options on mount / country change
  useEffect(() => {
    setCountryOptionsLoaded(false);
    setCountryOptions([]);
    if (!selectedCountry?.value) return undefined;
    let cancelled = false;
    (async () => {
      setCountryLoading(true);
      try {
        const res = await getCountryPaymentOptions(userToken, selectedCountry.value);
        if (!cancelled) {
          setCountryOptions(res.body?.paymentOptions || []);
          setCountryOptionsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setCountryOptions([]);
          setCountryOptionsLoaded(true);
        }
      } finally {
        if (!cancelled) setCountryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userToken, selectedCountry?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique media types from country options
  const countryMediaTypes = useMemo(
    () => [...new Set(countryOptions.map((o) => o.mediaType).filter(Boolean))],
    [countryOptions],
  );

  // Given a mediaType, return the matching paymentOptionTypes
  // Given a mediaType + paymentOptionType, return the matching paymentTypes
  const getOptionsForMedia = useCallback((mediaType) => {
    const filtered = countryOptions.filter((o) => o.mediaType === mediaType);
    return [...new Set(filtered.map((o) => o.paymentOptionType).filter(Boolean))];
  }, [countryOptions]);

  const getPaymentTypesFor = useCallback((mediaType, paymentOptionType) => {
    const filtered = countryOptions.filter(
      (o) => o.mediaType === mediaType && o.paymentOptionType === paymentOptionType,
    );
    return [...new Set(filtered.map((o) => o.paymentType).filter(Boolean))];
  }, [countryOptions]);

  // ---- Copy Store: auto-fetch on store selection ----

  useEffect(() => {
    if (!selectedSourceStore) {
      setSourceOptions(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setSourceLoading(true);
      setStatus(null);
      try {
        const res = await getStorePaymentOptions(userToken, selectedSourceStore.id);
        if (!cancelled) setSourceOptions(res.body?.paymentOptions || []);
      } catch (err) {
        if (!cancelled) {
          setStatus({ isError: true, message: `Failed to fetch source store: ${err?.message || 'Unknown error'}` });
          setSourceOptions(null);
        }
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userToken, selectedSourceStore]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedDestStore) {
      setDestOptions(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setDestLoading(true);
      try {
        const res = await getStorePaymentOptions(userToken, selectedDestStore.id);
        if (!cancelled) setDestOptions(res.body?.paymentOptions || []);
      } catch (err) {
        if (!cancelled) setDestOptions(null);
      } finally {
        if (!cancelled) setDestLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userToken, selectedDestStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverrideToggle = useCallback(() => {
    if (!override) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Enabling Override will permanently delete ALL existing payment options at the destination store before copying. Are you sure?',
      );
      if (!confirmed) return;
    }
    setOverride((prev) => !prev);
  }, [override]);

  const handleCopy = useCallback(async () => {
    if (!selectedSourceStore || !selectedDestStore) return;
    setLoading(true);
    setStatus(null);
    if (onSaveStatus) onSaveStatus(null);
    try {
      const body = {
        destStoreId: selectedDestStore.id,
        excludePaymentTypes: excludeTypes.length > 0 ? excludeTypes : undefined,
        override,
        sourceStoreId: selectedSourceStore.id,
      };
      const res = await copyStorePaymentOptions(userToken, body);
      const { copiedCount = 0, deletedCount = 0 } = res.body || {};
      const copyResult = {
        isError: false,
        message: `Copy succeeded — ${copiedCount} option(s) copied${override ? `, ${deletedCount} deleted` : ''}.`,
      };
      setStatus(copyResult);
      if (onSaveStatus) onSaveStatus(copyResult);
      // Re-fetch destination store options to show final state
      try {
        const destRes = await getStorePaymentOptions(userToken, selectedDestStore.id);
        setDestOptions(destRes.body?.paymentOptions || []);
      } catch {
        // silent — best effort refresh
      }
      if (hasNextTab && onSaveAndNext) {
        onSaveAndNext();
      }
    } catch (err) {
      const copyError = { isError: true, message: `Copy failed: ${err?.message || 'Unknown error'}` };
      setStatus(copyError);
      if (onSaveStatus) onSaveStatus(copyError);
    } finally {
      setLoading(false);
    }
  }, [userToken, selectedSourceStore, selectedDestStore, override, excludeTypes, hasNextTab, onSaveAndNext, onSaveStatus]);

  // ---- Segmented dest preview / result computation ----

  const previewSegments = useMemo(() => {
    if (!sourceOptions || !destOptions) return null;
    const destKeySet = new Set(destOptions.map(makeOptionKey));
    const effectiveSource = excludeTypes.length > 0
      ? sourceOptions.filter((o) => !excludeTypes.includes(o.paymentType))
      : sourceOptions;
    return {
      duplicatesSkipped: effectiveSource.filter((o) => destKeySet.has(makeOptionKey(o))),
      existing: destOptions,
      willBeCopied: effectiveSource.filter((o) => !destKeySet.has(makeOptionKey(o))),
    };
  }, [sourceOptions, destOptions, excludeTypes]);

  // ---- Configure Store handlers ----

  useEffect(() => {
    if (!selectedConfigStore) {
      setConfigFetched(false);
      setConfigRows([emptyRow()]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setConfigLoading(true);
      setStatus(null);
      setEmptyStoreMessage('');
      try {
        const res = await getStorePaymentOptions(userToken, selectedConfigStore.id);
        if (cancelled) return;
        const options = res.body?.paymentOptions || [];
        setConfigFetched(true);
        if (options.length > 0) {
          setConfigRows(options.map((o) => ({
            isExisting: true,
            mediaType: o.mediaType || '',
            paymentOptionType: o.paymentOptionType || '',
            paymentType: o.paymentType || '',
            rowId: uuidv4(),
          })));
        } else {
          setConfigRows([emptyRow()]);
          setEmptyStoreMessage('No payment options configured for this store yet.');
          setTimeout(() => { if (!cancelled) setEmptyStoreMessage(''); }, 5000);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({ isError: true, message: `Failed to fetch store: ${err?.message || 'Unknown error'}` });
          setConfigFetched(false);
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userToken, selectedConfigStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddRow = useCallback(() => {
    setConfigRows((prev) => [...prev, emptyRow()]);
  }, []);

  const handleRemoveRow = useCallback((index) => {
    setConfigRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyRow()];
    });
  }, []);

  const handleRowChange = useCallback((index, field, value) => {
    setConfigRows((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (field === 'mediaType') {
        return {
          ...row, mediaType: value, paymentOptionType: '', paymentType: '',
        };
      }
      if (field === 'paymentOptionType') {
        return { ...row, paymentOptionType: value, paymentType: '' };
      }
      return { ...row, [field]: value };
    }));
  }, []);

  const handleAddFromCountry = useCallback((opt) => {
    const candidate = {
      mediaType: opt.mediaType || '',
      paymentOptionType: opt.paymentOptionType || '',
      paymentType: opt.paymentType || '',
    };
    setConfigRows((prev) => {
      if (isDuplicateRow(prev, candidate)) return prev;
      const cleaned = prev.filter((r) => r.mediaType || r.paymentOptionType || r.paymentType);
      return [...cleaned, { ...candidate, rowId: uuidv4() }];
    });
  }, []);

  const handleSaveConfig = useCallback(async () => {
    const newRows = configRows
      .filter((r) => !r.isExisting && r.mediaType.trim() && r.paymentOptionType.trim());
    const validRows = newRows
      .map(({ mediaType, paymentOptionType, paymentType }) => ({
        mediaType, paymentOptionType, paymentType: paymentType || '',
      }));
    if (!selectedConfigStore || validRows.length === 0) return;
    setLoading(true);
    setStatus(null);
    if (onSaveStatus) onSaveStatus(null);
    try {
      const body = { paymentOptions: validRows };
      const res = await saveStorePaymentOptions(userToken, selectedConfigStore.id, body);
      const { savedCount = 0, skippedCount = 0 } = res.body || {};
      const saveResult = {
        isError: false,
        message: `Save succeeded — ${savedCount} option(s) saved, ${skippedCount} skipped (duplicates).`,
      };
      setStatus(saveResult);
      if (onSaveStatus) onSaveStatus(saveResult);
      // Mark newly saved rows as existing so they become read-only
      setConfigRows((prev) => prev.map((r) => (r.isExisting ? r : { ...r, isExisting: true })));
      if (hasNextTab && onSaveAndNext) {
        onSaveAndNext();
      }
    } catch (err) {
      const saveError = { isError: true, message: `Save failed: ${err?.message || 'Unknown error'}` };
      setStatus(saveError);
      if (onSaveStatus) onSaveStatus(saveError);
    } finally {
      setLoading(false);
    }
  }, [userToken, selectedConfigStore, configRows, hasNextTab, onSaveAndNext, onSaveStatus]);

  // ---- Render helpers ----

  const renderSegmentedControl = () => {
    const modes = [MODES.COPY, MODES.CONFIGURE];
    const activeIndex = modes.indexOf(mode);
    return (
      <div className="soa-workflow-section">
        <div className="soa-segmented">
          <div
            className="soa-segmented-indicator"
            style={{
              transform: `translateX(${activeIndex * 100}%)`,
              width: `calc((100% - 8px) / ${modes.length})`,
            }}
          />
          {modes.map((m) => (
            <button
              key={m}
              className={`soa-segmented-btn ${mode === m ? 'soa-segmented-btn-active' : ''}`}
              type="button"
              onClick={() => { setMode(m); setStatus(null); }}
            >
              <span className="soa-segmented-label">{MODE_LABELS[m]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderPaymentOptionsTable = (options, caption) => {
    if (!options || options.length === 0) {
      return <p className="soa-payment-empty">No payment options found{caption ? ` ${caption}` : ''}.</p>;
    }
    // Build unique keys — use paymentOptionId when available, otherwise deduplicate composite keys
    const keyCounts = {};
    const rowKeys = options.map((opt) => {
      if (opt.paymentOptionId) return opt.paymentOptionId;
      const base = `${opt.mediaType}-${opt.paymentOptionType}-${opt.paymentType}`;
      keyCounts[base] = (keyCounts[base] || 0) + 1;
      return `${base}--${keyCounts[base]}`;
    });
    return (
      <table className="soa-table soa-payment-table">
        <thead>
          <tr>
            <th>Media Type</th>
            <th>Payment Option Type</th>
            <th>Payment Type</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt, i) => (
            <tr key={rowKeys[i]} className={i % 2 === 0 ? '' : 'soa-row-even'}>
              <td>{opt.mediaType}</td>
              <td>
                <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
                  {opt.paymentOptionType}
                </span>
              </td>
              <td>{opt.paymentType}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderSegmentedDestTable = (segments, isResult) => {
    const {
      existing, duplicatesSkipped,
    } = segments;
    const newItems = isResult ? segments.newlyCopied : segments.willBeCopied;
    const newLabel = isResult ? 'Copied from Source' : 'Will Be Copied';
    const totalCount = existing.length + newItems.length;

    const renderRows = (rows, cssClass) => rows.map((opt, i) => {
      const rk = `${cssClass}-${makeOptionKey(opt)}-${i}`;
      return (
        <tr key={rk} className={`${i % 2 === 0 ? '' : 'soa-row-even'} ${cssClass}`}>
          <td>{opt.mediaType}</td>
          <td>
            <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
              {opt.paymentOptionType}
            </span>
          </td>
          <td>{opt.paymentType}</td>
        </tr>
      );
    });

    return (
      <div className="soa-payment-segmented-table">
        <div className="soa-payment-preview-title">
          {isResult ? 'Destination — After Copy' : 'Destination — Preview'} ({totalCount})
        </div>
        <div className="soa-payment-table-scroll">
          <table className="soa-table soa-payment-table">
            <thead>
              <tr>
                <th>Media Type</th>
                <th>Payment Option Type</th>
                <th>Payment Type</th>
              </tr>
            </thead>
            <tbody>
              {/* Segment 1: Existing */}
              {existing.length > 0 && (
                <>
                  <tr className="soa-payment-segment-header soa-payment-segment-existing">
                    <td colSpan="3">
                      Existing ({existing.length})
                    </td>
                  </tr>
                  {renderRows(existing, '')}
                </>
              )}

              {/* Segment 2: New / Copied */}
              {newItems.length > 0 && (
                <>
                  <tr className="soa-payment-segment-header soa-payment-segment-new">
                    <td colSpan="3">
                      {newLabel} ({newItems.length})
                    </td>
                  </tr>
                  {renderRows(newItems, 'soa-payment-row-new')}
                </>
              )}

              {/* Segment 3: Duplicates Skipped */}
              {duplicatesSkipped.length > 0 && (
                <>
                  <tr className="soa-payment-segment-header soa-payment-segment-skipped">
                    <td colSpan="3">
                      Duplicates Skipped ({duplicatesSkipped.length})
                    </td>
                  </tr>
                  {renderRows(duplicatesSkipped, 'soa-payment-row-skipped')}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCollapsibleHeader = (title, count, expanded, onToggle) => (
    <button
      className="soa-payment-collapsible-header"
      type="button"
      onClick={onToggle}
    >
      <span className={`soa-payment-collapsible-chevron ${expanded ? 'soa-payment-collapsible-chevron-open' : ''}`}>
        &#9654;
      </span>
      <span className="soa-payment-collapsible-title">{title}</span>
      {count !== undefined && (
        <span className="soa-payment-collapsible-count">({count})</span>
      )}
    </button>
  );

  const renderCountryOptionsInline = () => {
    if (!countryOptionsLoaded && countryLoading) {
      return <p className="soa-payment-hint">Loading country payment options...</p>;
    }
    if (!countryOptionsLoaded || countryOptions.length === 0) return null;

    return (
      <div className="soa-payment-collapsible-section">
        {renderCollapsibleHeader(
          `${selectedCountry?.value || 'Country'} Payment Options`,
          countryOptions.length,
          countryTableExpanded,
          () => setCountryTableExpanded((prev) => !prev),
        )}
        {countryTableExpanded && (
          <table className="soa-table soa-payment-table soa-payment-country-inline-table">
            <thead>
              <tr>
                <th>Media Type</th>
                <th>Payment Option Type</th>
                <th>Payment Type</th>
                <th aria-label="Add" style={{ width: '50px' }} />
              </tr>
            </thead>
            <tbody>
              {countryOptions.map((opt, i) => {
                const alreadyAdded = isDuplicateRow(configRows, opt);
                const countryRowKey = opt.paymentOptionId
                  || `${opt.mediaType}-${opt.paymentOptionType}-${opt.paymentType}`;
                return (
                  <tr key={countryRowKey} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                    <td>{opt.mediaType}</td>
                    <td>
                      <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
                        {opt.paymentOptionType}
                      </span>
                    </td>
                    <td>{opt.paymentType}</td>
                    <td>
                      <button
                        className={`soa-payment-add-from-country ${alreadyAdded ? 'soa-payment-add-from-country-added' : ''}`}
                        disabled={alreadyAdded}
                        title={alreadyAdded ? 'Already added' : 'Add to configuration'}
                        type="button"
                        onClick={() => handleAddFromCountry(opt)}
                      >
                        {alreadyAdded ? '✓' : '+'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderCopyStore = () => {
    const bothSelected = selectedSourceStore && selectedDestStore;
    return (
      <div className="soa-payment-form">
        {/* --- Side-by-side: Source (left) + Destination (right) --- */}
        <div className="soa-payment-copy-grid">
          {/* Source column */}
          <div className="soa-payment-copy-col">
            <div className="soa-payment-field">
              <span className="soa-payment-label">
                Source Store <span className="soa-payment-req">*</span>
              </span>
              <div className="soa-payment-input-row">
                <div className="soa-payment-store-select">
                  <StoreSelect
                    fullWidth
                    countryRestriction={selectedCountry || {}}
                    selectCountry={() => {}}
                    selectStore={setSelectedSourceStore}
                    selectedCountry={selectedCountry}
                    selectedStore={selectedSourceStore}
                  />
                </div>
              </div>
              <span className="soa-payment-hint">Store to copy payment options FROM.</span>
            </div>

            {sourceLoading && <p className="soa-payment-hint">Loading source store options...</p>}
            {sourceOptions !== null && !sourceLoading && (
              <div className="soa-payment-preview">
                <div className="soa-payment-preview-title">
                  Source Store Payment Options ({sourceOptions.length})
                </div>
                <div className="soa-payment-table-scroll">
                  {renderPaymentOptionsTable(sourceOptions, 'for source store')}
                </div>
              </div>
            )}
          </div>

          {/* Destination column */}
          <div className="soa-payment-copy-col">
            <div className="soa-payment-field">
              <span className="soa-payment-label">
                Destination Store <span className="soa-payment-req">*</span>
              </span>
              <div className="soa-payment-input-row">
                <div className="soa-payment-store-select">
                  <StoreSelect
                    fullWidth
                    countryRestriction={selectedCountry || {}}
                    selectCountry={() => {}}
                    selectStore={setSelectedDestStore}
                    selectedCountry={selectedCountry}
                    selectedStore={selectedDestStore}
                  />
                </div>
              </div>
              <span className="soa-payment-hint">Store to copy payment options TO.</span>
            </div>

            {destLoading && <p className="soa-payment-hint">Loading destination store options...</p>}
            {/* Segmented preview before copy */}
            {previewSegments && !destLoading && (
              renderSegmentedDestTable(previewSegments, false)
            )}
            {/* Plain dest table when no source selected yet or after copy */}
            {destOptions !== null && !destLoading
              && !previewSegments && (
              <div className="soa-payment-preview">
                <div className="soa-payment-preview-title">
                  Destination — Existing Payment Options ({destOptions.length})
                </div>
                <div className="soa-payment-table-scroll">
                  {renderPaymentOptionsTable(destOptions, 'for destination store')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* --- Copy controls (full width) --- */}
        {bothSelected && (
          <>
            <div className="soa-payment-field">
              <span className="soa-payment-label">Override Existing Options</span>
              <div className="soa-payment-toggle-row">
                <button
                  aria-checked={override}
                  aria-label="Override existing options"
                  className={`soa-payment-switch ${override ? 'soa-payment-switch-on' : ''}`}
                  role="switch"
                  type="button"
                  onClick={handleOverrideToggle}
                >
                  <span className="soa-payment-switch-knob" />
                </button>
                <span className="soa-payment-toggle-label">
                  Override: {override ? 'ON' : 'OFF'}
                </span>
              </div>
              {override && (
                <div className="soa-payment-override-warning">
                  All existing payment options at the destination store will be deleted before copying.
                </div>
              )}
            </div>

            <div className="soa-payment-field">
              <span className="soa-payment-label">Exclude Payment Types</span>
              {sourceOptions !== null && sourceOptions.length > 0 ? (
                <div className="soa-payment-checkbox-list">
                  {[...new Set(sourceOptions.map((opt) => opt.paymentType))].map((type) => (
                    <label key={type} className="soa-payment-checkbox-item">
                      <input
                        checked={excludeTypes.includes(type)}
                        type="checkbox"
                        onChange={() => {
                          setExcludeTypes((prev) => (prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]));
                        }}
                      />
                      <span className="soa-payment-checkbox-label">{type}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <span className="soa-payment-hint">Select a source store to see available payment types.</span>
              )}
              <span className="soa-payment-hint">Payment types to skip during the copy.</span>
            </div>

            <div className="soa-payment-actions">
              <ButtonBlack
                isDisabled={!selectedSourceStore || !selectedDestStore || loading}
                isLoading={loading}
                label={hasNextTab ? 'Save and Next' : 'Save'}
                onClick={handleCopy}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderConfigureStore = () => {
    const newValidRows = configRows.filter(
      (r) => !r.isExisting && r.mediaType.trim() && r.paymentOptionType.trim(),
    );
    const canSave = selectedConfigStore && newValidRows.length > 0 && !loading;
    return (
      <div className="soa-payment-form soa-payment-form-narrow">
        <div className="soa-payment-field">
          <span className="soa-payment-label">
            Store <span className="soa-payment-req">*</span>
          </span>
          <div className="soa-payment-input-row">
            <div className="soa-payment-store-select">
              <StoreSelect
                fullWidth
                countryRestriction={selectedCountry || {}}
                selectCountry={() => {}}
                selectStore={setSelectedConfigStore}
                selectedCountry={selectedCountry}
                selectedStore={selectedConfigStore}
              />
            </div>
          </div>
          <span className="soa-payment-hint">The store being configured.</span>
          {configLoading && <p className="soa-payment-hint">Loading store payment options...</p>}
        </div>

        {emptyStoreMessage && (
          <div className="soa-payment-empty-state">{emptyStoreMessage}</div>
        )}

        {configFetched && (
          <>
            {/* Inline country options with + buttons */}
            {renderCountryOptionsInline()}

            {/* Store config table */}
            <div className="soa-payment-collapsible-section">
              {renderCollapsibleHeader(
                'Store Payment Options',
                configRows.filter((r) => r.mediaType || r.paymentOptionType || r.paymentType).length,
                configTableExpanded,
                () => setConfigTableExpanded((prev) => !prev),
              )}
              {configTableExpanded && (
              <>
                <table className="soa-table soa-payment-table soa-payment-edit-table">
                  <thead>
                    <tr>
                      <th>Media Type</th>
                      <th>Payment Option Type</th>
                      <th>Payment Type</th>
                      <th aria-label="Actions" style={{ width: '80px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {configRows.map((row, i) => {
                    if (row.isExisting) {
                      return (
                        <tr key={row.rowId} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                          <td>{row.mediaType}</td>
                          <td>{row.paymentOptionType}</td>
                          <td>{row.paymentType}</td>
                          <td aria-label="No actions" />
                        </tr>
                      );
                    }

                    const mediaSelected = !!row.mediaType;
                    const potSelected = !!row.paymentOptionType;

                    const countryPotOpts = mediaSelected ? getOptionsForMedia(row.mediaType) : [];
                    const countryPtOpts = mediaSelected && potSelected
                      ? getPaymentTypesFor(row.mediaType, row.paymentOptionType)
                      : [];

                    const mediaOpts = countryMediaTypes.includes(row.mediaType)
                      ? countryMediaTypes
                      : [...countryMediaTypes, ...(row.mediaType ? [row.mediaType] : [])];
                    const potOpts = countryPotOpts.includes(row.paymentOptionType)
                      ? countryPotOpts
                      : [...countryPotOpts, ...(row.paymentOptionType ? [row.paymentOptionType] : [])];
                    const ptOpts = countryPtOpts.includes(row.paymentType)
                      ? countryPtOpts
                      : [...countryPtOpts, ...(row.paymentType ? [row.paymentType] : [])];

                    return (
                      <tr key={row.rowId} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                        {/* Media Type */}
                        <td>
                          <select
                            aria-label={`Media type for row ${i + 1}`}
                            className="soa-payment-select"
                            value={row.mediaType}
                            onChange={(e) => handleRowChange(i, 'mediaType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {mediaOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        {/* Payment Option Type — disabled until media type selected */}
                        <td>
                          <select
                            aria-label={`Payment option type for row ${i + 1}`}
                            className={`soa-payment-select ${!mediaSelected ? 'soa-payment-select-disabled' : ''}`}
                            disabled={!mediaSelected}
                            value={row.paymentOptionType}
                            onChange={(e) => handleRowChange(i, 'paymentOptionType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {potOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        {/* Payment Type — disabled until payment option type selected */}
                        <td>
                          <select
                            aria-label={`Payment type for row ${i + 1}`}
                            className={`soa-payment-select ${!potSelected ? 'soa-payment-select-disabled' : ''}`}
                            disabled={!potSelected}
                            value={row.paymentType}
                            onChange={(e) => handleRowChange(i, 'paymentType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {ptOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <button
                            className="soa-payment-remove-btn"
                            type="button"
                            onClick={() => handleRemoveRow(i)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>

                <div className="soa-payment-table-actions">
                  <button className="soa-payment-add-row" type="button" onClick={handleAddRow}>
                    + Add another payment option
                  </button>
                </div>
              </>
              )}
            </div>

            <div className="soa-payment-actions">
              <ButtonBlack
                isDisabled={!canSave}
                isLoading={loading}
                label={hasNextTab ? 'Save and Next' : 'Save Payment Options'}
                onClick={handleSaveConfig}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <section className="soa-payment-tab">
      {renderSegmentedControl()}

      {mode === MODES.COPY && renderCopyStore()}
      {mode === MODES.CONFIGURE && renderConfigureStore()}
    </section>
  );
};

PaymentConfigTab.defaultProps = {
  hasNextTab: false,
  onSaveAndNext: null,
  onSaveStatus: null,
  selectedCountry: null,
};

PaymentConfigTab.propTypes = {
  hasNextTab: PropTypes.bool,
  onSaveAndNext: PropTypes.func,
  onSaveStatus: PropTypes.func,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

export default PaymentConfigTab;
